import React, { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useWizard } from '../../context/WizardContext';
import AdminFields from '../../components/common/OrganizationForm/AdminFields';
import { PrimaryButton, SecondaryButton } from '../../components/common/ButtonsV2';

/**
 * StepOrganizationAdminContact is a form step that collects the primary
 * administrator's contact details, such as full name, phone number,
 * email, and role. It validates the inputs and controls navigation.
 *
 * @param {Object} props
 * @param {boolean} props.isSubmitting - Indicates whether the form is currently being submitted.
 */
const StepOrganizationAdminContact = ({ isSubmitting }) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const { prev, setStepValid, canProceed } = useWizard();

  const [name, phone, email, role] = useWatch({
    control,
    name: [
      'primary_admin_full_name',
      'primary_admin_phone_number',
      'primary_admin_email',
      'primary_admin_role',
    ],
  });

  useEffect(() => {
    const allFilled = [name, phone, email, role].every(
      (val) => typeof val === 'string' && val.trim() !== '',
    );
    const hasErrors = Object.keys(errors).length > 0;
    const isValid = allFilled && !hasErrors;

    if (canProceed !== isValid) {
      setStepValid(isValid);
    }
  }, [name, phone, email, role, errors, canProceed, setStepValid]);

  return (
    <div className="space-y-6 bg-white p-[50px] rounded-[10px] max-w-[626px] mt-[20px] w-full">
      <h2 className="text-xl font-bold">Primary Administrator Contact</h2>

      {/* Form fields for admin contact information */}
      <AdminFields />

      {/* Navigation buttons: Previous and Submit */}
      <div className="flex justify-between grid grid-cols-2 gap-4 !mt-[40px]">
        <SecondaryButton fullWidth onClick={prev}>
          Previous
        </SecondaryButton>
        <PrimaryButton fullWidth type="submit" disabled={!canProceed || isSubmitting}>
          {isSubmitting ? 'Creating…' : 'Create Organization'}
        </PrimaryButton>
      </div>
    </div>
  );
};

export default StepOrganizationAdminContact;
